import { Copy16, Logout16 } from '@carbon/icons-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Comment, ContextType, ConversationAPI } from 'api/conversations';
import { ProjectConversationId, UserType } from 'api/models';
import { PinScopeType, PinResourceType } from 'api/spotlight/types';
import {
  CpBody00Secondary,
  CpBody01Primary,
} from 'components/atoms/customer-portal/typography';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { ConversationPane } from 'components/organisms/conversations';
import { ConversationSkeleton } from 'components/organisms/conversations/loading';
import { MessagesComment } from 'components/organisms/conversations/messages-comment';
import { CommentSeenBy } from 'components/organisms/projects/project-conversation/conversation-components/conversation-pane/comment-seen-by';
import { CPJoinChannel } from 'components/organisms/projects/project-conversation/conversation-components/conversation-pane/join-conversation';
import { MessageFooter } from 'components/organisms/projects/project-conversation/conversation-components/conversation-pane/message-footer';
import { SpotlightGroup } from 'components/organisms/spotlight/spotlight-in-tag';
import { useAccount } from 'hooks/use-account';
import { useGetConversationWithWS } from 'hooks/use-conversations';
import { useGetSpotlights } from 'hooks/use-spotlight';
import { useUser } from 'hooks/use-user';
import {
  useDeleteProjectConversationsMutation,
  useRemoveMembersProjectConversationsMutation,
} from 'queries/project-wide-conversation';
import { isCustomerPortalInBuilderMode } from 'utils/portal-utils';
import { CpParaMedium } from '../../components/typography/typo';
import { CpDeleteChatModal } from './cp-delete-chat-modal';
import { CpLeaveChatModal } from './cp-leave-chat-modal';
import { CpChatRenderInput } from './types';

export type CpCOnversationPaneContext = {
  id: number;
  projectId: number;
  type: keyof typeof ContextType;
  name: string;
  desc?: string;
};

const ChatPaneWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background: white;
  display: flex;
  flex-direction: column;

  /* no padding for 480px */
  @media (max-width: 480px) {
    padding: 0;
  }

  [data-preview-mode='false'] & {
    height: 55vh;
  }
`;

const ConversationPaneHeader = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  padding-top: var(--cp-spacing-05);
  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-05);
  padding-bottom: var(--cp-spacing-05);
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 480px) {
    padding: var(--cp-spacing-03) var(--cp-spacing-05);
  }
`;

const ConversationPaneTitleAndDescHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto; /* Allow the item to grow and shrink */
  overflow: hidden; /* Hide overflow text */
  white-space: nowrap; /* Prevent text from wrapping */
  text-overflow: ellipsis; /* Add ellipsis for overflow text */
  min-height: 32px;
  ${CpBody01Primary}, ${CpBody00Secondary} {
    width: 100%;
  }
  /* ${CpParaMedium} {
    color: black;
    font-weight: 600;
  } */
  ${CpBody00Secondary} {
    flex: 1 1 auto; /* Allow the item to grow and shrink */
    overflow: hidden; /* Hide overflow text */
    white-space: normal; /* Prevent text from wrapping */
    /* ellipsize after two lines */

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    box-orient: vertical;
    line-clamp: 2;
    overflow: hidden;
  }
  ${CpBody01Primary} {
    @media (max-width: 480px) {
      overflow: hidden; /* Hide overflow text */
      white-space: nowrap; /* Prevent text from wrapping */
      text-overflow: ellipsis; /* Add ellipsis for overflow text */
    }
  }
`;

const SpotlightGroupWrapper = styled.div`
  width: 100%;
  margin-top: var(--cp-spacing-03);

  /* hide in mobile */
  @media (max-width: 480px) {
    display: none;
  }
`;

const ConversationPaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  .customer-portal-conversation-comment-box {
    margin: var(--spacing-06);
    margin-top: 0;
  }
  .ant-comment-inner .ant-comment-avatar {
    width: 56px;
  }
  @media (max-width: 480px) {
    .customer-portal-conversation-comment-box {
      margin: var(--spacing-05);
      margin-bottom: var(--spacing-07);
      margin-top: 0;
    }
    .ant-comment-inner .ant-comment-avatar {
      width: 48px;
    }
  }
`;

const getSpotlightContextForConversationContext = (
  args:
    | {
        id: number;
        type: keyof typeof ContextType;
        pinScopeId?: number;
        pinScopeType?: PinScopeType;
      }
    | undefined,
) => {
  if (!args) {
    return undefined;
  }
  const { id, type, pinScopeId, pinScopeType } = args;

  if (pinScopeId != null && pinScopeType != null) {
    return {
      scopeType: pinScopeType,
      scopeId: pinScopeId,
      resourceType: PinResourceType.Chat,
    };
  }

  switch (type) {
    case 'PROJECT_CONVERSATIONS':
      return {
        scopeType: PinScopeType.ProjectConversation,
        scopeId: id,
        resourceType: PinResourceType.Chat,
      };

    case 'TASK':
      return {
        scopeType: PinScopeType.Task,
        scopeId: id,
        resourceType: PinResourceType.Chat,
      };
    case 'SPACE_TAB':
    case 'DOCUMENT':
      return {
        scopeType: PinScopeType.Document,
        scopeId: id,
        resourceType: PinResourceType.Chat,
      };
    default:
      return undefined;
  }
};

export type CpConversationPaneProps = {
  activeContext: CpCOnversationPaneContext | undefined;
  onFilterChange?: (args: Record<string, unknown>) => void;
  setActiveContext: (c: CpCOnversationPaneContext | undefined) => void;
  data: CpChatRenderInput;
  setCreateNewConversationInProject: React.Dispatch<
    React.SetStateAction<
      | {
          projectId: number;
          name: string;
          desc?: string | undefined;
          entityId?: number | undefined;
          entityType?:
            | 'TASK'
            | 'DOCUMENT'
            | 'SPACE_TAB'
            | 'PROJECT_CONVERSATIONS'
            | 'ATTACHMENTS'
            | 'RESOURCE_REQUEST'
            | 'INVOICE'
            | undefined;
        }
      | undefined
    >
  >;
};

export const CpConversationPane: React.FC<CpConversationPaneProps> = ({
  activeContext,
  onFilterChange,
  setActiveContext,
  data,
  setCreateNewConversationInProject,
}) => {
  const { user } = useUser();
  const { accountId } = useAccount();
  const userId = useMemo(() => user?.userId, [user?.userId]);
  const upatedLastSeenRef = useRef(false);
  const [leaveConversationModalVisible, setLeaveConversationModalVisible] =
    useState(false);
  const [deleteConversationModalVisible, setDeleteConversationModalVisible] =
    useState(false);

  const { data: spotlights = [] } = useGetSpotlights(
    getSpotlightContextForConversationContext(activeContext),
  );

  const { mutateAsync: muatateLeaveConversation } =
    useRemoveMembersProjectConversationsMutation([
      'project_id',
      activeContext?.projectId as number,
    ]);
  const { mutateAsync: muatateDeleteConversation } =
    useDeleteProjectConversationsMutation([
      'project_id',
      activeContext?.projectId as number,
    ]);

  const [userComments, setUserComments] = useState<Comment[]>([]);
  const context = activeContext
    ? {
        type: activeContext.type,
        id: activeContext.id,
      }
    : undefined;
  const { data: convData } = useGetConversationWithWS(
    activeContext?.projectId as number,
    context,
  );
  const members = useMemo(() => convData?.members, [convData]);

  const memberDetails = (members || []).find(
    (memberObject) => memberObject?.user?.userId === userId,
  );

  const currentLastReadCommentId = memberDetails?.lastSeenCommentCreatedAt;
  // Store the older commentId between renders so that when currentLastReadCommentId changes through socket,
  // we can still refer the old lastReadCommentId.
  const [lastReadComment, setLastComment] =
    useState<
      | undefined
      | {
          commentId: number;
          convId: number | undefined;
        }
    >();

  useEffect(() => {
    if (!currentLastReadCommentId) {
      return;
    }

    if (
      !lastReadComment?.commentId ||
      lastReadComment?.convId !== activeContext?.id
    ) {
      setLastComment({
        commentId: currentLastReadCommentId,
        convId: activeContext?.id,
      });
    }
  }, [currentLastReadCommentId, lastReadComment, activeContext?.id]);

  const currentUserLastReadCommentIndex = userComments.findIndex(
    (cmnt) => cmnt.commentId === lastReadComment?.commentId,
  );
  const tailMessages = userComments.slice(currentUserLastReadCommentIndex + 1);
  // No need to show the hook if the lastMessages were from the activeUser itself
  const shouldRenderNewMessagesMarker = tailMessages.find(
    (cmnt) => cmnt.user.userId !== userId,
  );

  const lastComment = userComments[userComments.length - 1];

  const onConversationCommentsLoaded = useCallback((comments: Comment[]) => {
    if (comments.length) {
      setUserComments(comments);
    }
  }, []);

  const renderMessageFooterExtra = useCallback(
    (comment) =>
      shouldRenderNewMessagesMarker &&
      lastComment?.commentId !== lastReadComment?.commentId &&
      currentUserLastReadCommentIndex > -1 ? (
        <MessageFooter
          currentComment={comment}
          lastReadCommentId={lastReadComment?.commentId}
        />
      ) : null,
    [
      lastReadComment,
      lastComment,
      shouldRenderNewMessagesMarker,
      currentUserLastReadCommentIndex,
    ],
  );

  const renderMessageTailComponent = useCallback(
    (currentComment: Comment) => {
      if (!members) {
        return null;
      }

      const seenUsers =
        members.filter(
          (member) =>
            member?.user &&
            userId !== member?.user?.userId &&
            currentComment?.commentId &&
            currentComment?.createdAt === member?.lastSeenCommentCreatedAt,
        ) ?? [];

      return seenUsers.length ? <CommentSeenBy seenUsers={seenUsers} /> : null;
    },
    [members, userId],
  );

  const renderMessagesComponent = useCallback(() => {
    const projectId = activeContext?.projectId;

    if (user?.userType === UserType.CustomerPortalBot && projectId) {
      return (
        <MessagesComment
          actionsOnLeft
          className="customer-portal-conversation-comment-box"
          messageContent=""
          placeholder=""
          hidePrivate
          readonly
          handleUserMention={() => {}}
          accountId={accountId}
          projectId={projectId}
          onAddComment={() => {}}
          onClearReply={() => {}}
          hasAccessToCreatePrivateCommentsOnly={false}
        />
      );
    }

    if (activeContext?.type !== 'PROJECT_CONVERSATIONS') {
      return undefined;
    }

    const activeConversation = data.projectChats
      ?.flatMap((e) => e?.projectConversations)
      ?.find((e) => e.conversationId === activeContext?.id);

    return (
      activeConversation &&
      !(activeConversation as any)?.isPartOfConversation && (
        <CPJoinChannel
          onSuccess={() => onFilterChange?.({})}
          conversation={activeConversation}
          projectId={['project_id', activeConversation?.project?.projectId]}
        />
      )
    );
  }, [
    accountId,
    activeContext?.id,
    activeContext?.projectId,
    activeContext?.type,
    data.projectChats,
    onFilterChange,
    user?.userType,
  ]);

  const onLeave = useCallback(async () => {
    setLeaveConversationModalVisible(false);

    if (
      activeContext?.type === 'PROJECT_CONVERSATIONS' &&
      activeContext?.projectId &&
      userId
    ) {
      await muatateLeaveConversation({
        users: [
          {
            userId,
          },
        ],
        conversationId: ProjectConversationId(activeContext?.id as number),
      });

      onFilterChange?.({});
    }

    return Promise.resolve();
  }, [
    activeContext?.id,
    activeContext?.projectId,
    activeContext?.type,
    muatateLeaveConversation,
    onFilterChange,
    userId,
  ]);

  const onDelete = useCallback(async () => {
    setDeleteConversationModalVisible(false);

    if (
      activeContext?.type === 'PROJECT_CONVERSATIONS' &&
      activeContext?.projectId &&
      userId
    ) {
      await muatateDeleteConversation(
        ProjectConversationId(activeContext?.id as number),
      );
      setActiveContext(undefined);
      onFilterChange?.({});
    }

    return Promise.resolve();
  }, [
    activeContext?.id,
    activeContext?.projectId,
    activeContext?.type,
    muatateDeleteConversation,
    onFilterChange,
    setActiveContext,
    userId,
  ]);

  const onUpdateLastSeen = useCallback(() => {
    if (!upatedLastSeenRef.current) {
      upatedLastSeenRef.current = true;
      onFilterChange?.({});
    }
  }, [onFilterChange]);

  return (
    <>
      <ChatPaneWrapper>
        {activeContext ? (
          <>
            <ConversationPaneHeader>
              <ConversationPaneTitleAndDescHeader>
                <CpBody01Primary>{activeContext.name}</CpBody01Primary>
                {activeContext?.desc ? (
                  <CpBody00Secondary>{activeContext.desc}</CpBody00Secondary>
                ) : (
                  <> </>
                )}
                {spotlights.length > 0 ? (
                  <SpotlightGroupWrapper>
                    <SpotlightGroup
                      spotlights={spotlights}
                      onSpotlightClick={(s) => {
                        document
                          .querySelector(`[data-spotlight='${s?.resourceId}']`)
                          ?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                            inline: 'nearest',
                          });
                      }}
                    />
                  </SpotlightGroupWrapper>
                ) : (
                  <></>
                )}
              </ConversationPaneTitleAndDescHeader>
              <MoreActionsDropdown
                getPopupContainer={() =>
                  document.querySelector('.rl-cp-page') || document.body
                }
                menu={
                  <Menu>
                    <Menu.Header>
                      <FormattedMessage id="more_actions" />
                    </Menu.Header>
                    {/* <Menu.Item icon={<UserFollow16 />}>
                      <FormattedMessage id="projects.conversations.members.add_people" />
                    </Menu.Item> */}
                    <Menu.Item
                      onClick={() => {
                        navigator.clipboard.writeText(
                          window.location.href.replace(/\/$/, ''),
                        );
                      }}
                      icon={<Copy16 />}
                    >
                      <FormattedMessage id="projects.conversations.copy_link" />
                    </Menu.Item>
                    {activeContext?.type === 'PROJECT_CONVERSATIONS' ? (
                      <Menu.ItemEdit
                        onClick={() => {
                          if (activeContext) {
                            setCreateNewConversationInProject({
                              projectId: activeContext?.projectId,
                              name: activeContext?.name,
                              desc: activeContext?.desc,
                              entityId: activeContext?.id,
                              entityType: activeContext?.type,
                            });
                          }
                        }}
                      >
                        <FormattedMessage id="projects.conversations.edit_details" />
                      </Menu.ItemEdit>
                    ) : (
                      <></>
                    )}
                    <Menu.Divider />
                    {!renderMessagesComponent() ? (
                      <Menu.Item
                        icon={<Logout16 />}
                        onClick={() => setLeaveConversationModalVisible(true)}
                      >
                        <FormattedMessage id="projects.conversations.members.leave" />
                      </Menu.Item>
                    ) : (
                      <></>
                    )}
                    <Menu.ItemDelete
                      onClick={() => setDeleteConversationModalVisible(true)}
                    >
                      <FormattedMessage id="projects.conversations.delete" />
                    </Menu.ItemDelete>
                  </Menu>
                }
              />
            </ConversationPaneHeader>
            <ConversationPaneWrapper>
              <ConversationPane
                commentBoxConfig={{
                  hidePrivate: true,
                  hideSmartfills: true,
                  readonly: isCustomerPortalInBuilderMode(),
                  className: 'customer-portal-conversation-comment-box',
                }}
                context={activeContext}
                updateLastSeen
                spotlights={spotlights}
                projectId={activeContext.projectId}
                accountId={accountId}
                apiService={ConversationAPI}
                hideAuditLog
                actionsOnLeft
                messagesComponent={renderMessagesComponent()}
                lastSeenUpdateCallback={onUpdateLastSeen}
                onCommentsLoaded={onConversationCommentsLoaded}
                renderMessageFooterExtra={renderMessageFooterExtra}
                renderMessageTailComponent={renderMessageTailComponent}
              />
            </ConversationPaneWrapper>
          </>
        ) : (
          <ConversationSkeleton />
        )}
      </ChatPaneWrapper>

      <CpLeaveChatModal
        visible={leaveConversationModalVisible}
        onOk={onLeave}
        onCancel={() => setLeaveConversationModalVisible(false)}
      />
      <CpDeleteChatModal
        visible={deleteConversationModalVisible}
        onOk={onDelete}
        onCancel={() => setDeleteConversationModalVisible(false)}
      />
    </>
  );
};
